import { 
  DashboardMetric, 
  TimeSeriesData, 
  ChartDataPoint,
  AIInsight,
  SalesData,
  MarketingData,
  CustomerData,
  DataSource 
} from '../types';

// Dashboard Metrics
export const mockMetrics: DashboardMetric[] = [
  {
    id: 'revenue',
    label: 'Total Revenue',
    value: 124567.89,
    change: 12.5,
    trend: 'up',
    format: 'currency',
    icon: '💰'
  },
  {
    id: 'orders',
    label: 'Total Orders',
    value: 1458,
    change: 8.3,
    trend: 'up',
    format: 'number',
    icon: '📦'
  },
  {
    id: 'customers',
    label: 'Active Customers',
    value: 8934,
    change: -2.4,
    trend: 'down',
    format: 'number',
    icon: '👥'
  },
  {
    id: 'conversion',
    label: 'Conversion Rate',
    value: 3.42,
    change: 0.8,
    trend: 'up',
    format: 'percentage',
    icon: '🎯'
  },
  {
    id: 'aov',
    label: 'Avg Order Value',
    value: 85.43,
    change: 5.2,
    trend: 'up',
    format: 'currency',
    icon: '💳'
  },
  {
    id: 'satisfaction',
    label: 'Customer Satisfaction',
    value: 94.5,
    change: 1.2,
    trend: 'up',
    format: 'percentage',
    icon: '⭐'
  }
];

// Time Series Data for Line Charts
export const mockRevenueTimeSeries: TimeSeriesData[] = [
  { timestamp: '2024-01', label: 'Jan', value: 85000 },
  { timestamp: '2024-02', label: 'Feb', value: 92000 },
  { timestamp: '2024-03', label: 'Mar', value: 88000 },
  { timestamp: '2024-04', label: 'Apr', value: 98000 },
  { timestamp: '2024-05', label: 'May', value: 105000 },
  { timestamp: '2024-06', label: 'Jun', value: 115000 },
  { timestamp: '2024-07', label: 'Jul', value: 122000 },
  { timestamp: '2024-08', label: 'Aug', value: 118000 },
  { timestamp: '2024-09', label: 'Sep', value: 128000 },
  { timestamp: '2024-10', label: 'Oct', value: 135000 }
];

export const mockOrdersTimeSeries: TimeSeriesData[] = [
  { timestamp: '2024-01', label: 'Jan', value: 1120 },
  { timestamp: '2024-02', label: 'Feb', value: 1250 },
  { timestamp: '2024-03', label: 'Mar', value: 1180 },
  { timestamp: '2024-04', label: 'Apr', value: 1340 },
  { timestamp: '2024-05', label: 'May', value: 1420 },
  { timestamp: '2024-06', label: 'Jun', value: 1580 },
  { timestamp: '2024-07', label: 'Jul', value: 1650 },
  { timestamp: '2024-08', label: 'Aug', value: 1590 },
  { timestamp: '2024-09', label: 'Sep', value: 1720 },
  { timestamp: '2024-10', label: 'Oct', value: 1800 }
];

// Category Performance Data
export const mockCategoryData: ChartDataPoint[] = [
  { label: 'Jewelry', value: 45000 },
  { label: 'Watches', value: 32000 },
  { label: 'Accessories', value: 28000 },
  { label: 'Premium', value: 19500 }
];

// Sales by Region
export const mockRegionData: ChartDataPoint[] = [
  { label: 'North America', value: 52000 },
  { label: 'Europe', value: 38000 },
  { label: 'Asia Pacific', value: 24500 },
  { label: 'Latin America', value: 9500 }
];

// Top Products
export const mockTopProducts: ChartDataPoint[] = [
  { label: 'Gold Chain Premium', value: 15600 },
  { label: 'Diamond Ring Set', value: 12800 },
  { label: 'Silver Watch Elite', value: 11200 },
  { label: 'Pearl Necklace', value: 9800 },
  { label: 'Emerald Earrings', value: 8500 }
];

// AI Insights
export const mockInsights: AIInsight[] = [
  {
    id: 'insight-1',
    type: 'trend',
    title: 'Revenue Growth Acceleration',
    description: 'Revenue has increased by 15% over the last 30 days, significantly outpacing the previous period. This growth is primarily driven by increased sales in the premium jewelry category.',
    confidence: 92,
    impact: 'high',
    timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    actionable: true
  },
  {
    id: 'insight-2',
    type: 'anomaly',
    title: 'Unusual Spike in Cart Abandonment',
    description: 'Cart abandonment rate has increased to 72% from the usual 65%. This spike coincides with a recent price adjustment in shipping costs.',
    confidence: 88,
    impact: 'high',
    timestamp: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
    actionable: true
  },
  {
    id: 'insight-3',
    type: 'recommendation',
    title: 'Optimize Inventory for Holiday Season',
    description: 'Based on historical trends, demand for gold and diamond jewelry typically increases by 40% in Q4. Consider increasing inventory levels by 35% for these categories.',
    confidence: 85,
    impact: 'medium',
    timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
    actionable: true
  },
  {
    id: 'insight-4',
    type: 'forecast',
    title: 'Predicted Revenue for Next Month',
    description: 'Based on current trends and seasonality patterns, revenue is projected to reach $148,000 (+9% growth) in the next 30 days.',
    confidence: 78,
    impact: 'medium',
    timestamp: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
    actionable: false
  },
  {
    id: 'insight-5',
    type: 'recommendation',
    title: 'Launch Email Campaign for Inactive Customers',
    description: '15% of customers (1,340 users) haven\'t made a purchase in 90+ days. A targeted email campaign with a 10% discount could re-engage approximately 18% of them.',
    confidence: 81,
    impact: 'low',
    timestamp: new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString(),
    actionable: true
  }
];

// Sales Data for Tables
export const mockSalesData: SalesData[] = [
  {
    id: 'sale-1',
    productId: 'prod-101',
    productName: 'Gold Chain Premium',
    category: 'Jewelry',
    quantity: 3,
    revenue: 899.97,
    cost: 450.00,
    profit: 449.97,
    date: '2024-10-15',
    customer: 'John Doe',
    region: 'North America'
  },
  {
    id: 'sale-2',
    productId: 'prod-102',
    productName: 'Diamond Ring Set',
    category: 'Rings',
    quantity: 1,
    revenue: 1299.99,
    cost: 780.00,
    profit: 519.99,
    date: '2024-10-14',
    customer: 'Jane Smith',
    region: 'Europe'
  },
  {
    id: 'sale-3',
    productId: 'prod-103',
    productName: 'Silver Watch Elite',
    category: 'Watches',
    quantity: 2,
    revenue: 498.00,
    cost: 280.00,
    profit: 218.00,
    date: '2024-10-14',
    customer: 'Mike Johnson',
    region: 'Asia Pacific'
  },
  {
    id: 'sale-4',
    productId: 'prod-104',
    productName: 'Pearl Necklace',
    category: 'Jewelry',
    quantity: 4,
    revenue: 319.96,
    cost: 160.00,
    profit: 159.96,
    date: '2024-10-13',
    customer: 'Sarah Williams',
    region: 'North America'
  },
  {
    id: 'sale-5',
    productId: 'prod-105',
    productName: 'Emerald Earrings',
    category: 'Earrings',
    quantity: 2,
    revenue: 599.98,
    cost: 340.00,
    profit: 259.98,
    date: '2024-10-13',
    customer: 'Robert Brown',
    region: 'Europe'
  }
];

// Marketing Data
export const mockMarketingData: MarketingData[] = [
  {
    id: 'camp-1',
    campaign: 'Holiday Sale 2024',
    channel: 'Facebook Ads',
    impressions: 125000,
    clicks: 3750,
    conversions: 186,
    cost: 2500,
    revenue: 15480,
    roi: 519.2,
    date: '2024-10-15'
  },
  {
    id: 'camp-2',
    campaign: 'Email Newsletter Oct',
    channel: 'Email',
    impressions: 45000,
    clicks: 4500,
    conversions: 315,
    cost: 150,
    revenue: 22050,
    roi: 14600,
    date: '2024-10-10'
  },
  {
    id: 'camp-3',
    campaign: 'Google Search Ads',
    channel: 'Google Ads',
    impressions: 85000,
    clicks: 2550,
    conversions: 153,
    cost: 1800,
    revenue: 12750,
    roi: 608.3,
    date: '2024-10-12'
  }
];

// Customer Data
export const mockCustomerData: CustomerData[] = [
  {
    id: 'cust-1',
    name: 'John Doe',
    email: 'john.doe@example.com',
    joinDate: '2023-03-15',
    totalSpent: 2450.50,
    orderCount: 8,
    lastOrderDate: '2024-10-10',
    segment: 'vip',
    lifetimeValue: 3200
  },
  {
    id: 'cust-2',
    name: 'Jane Smith',
    email: 'jane.smith@example.com',
    joinDate: '2023-07-22',
    totalSpent: 1899.99,
    orderCount: 5,
    lastOrderDate: '2024-10-08',
    segment: 'regular',
    lifetimeValue: 2400
  },
  {
    id: 'cust-3',
    name: 'Mike Johnson',
    email: 'mike.j@example.com',
    joinDate: '2024-09-05',
    totalSpent: 350.00,
    orderCount: 2,
    lastOrderDate: '2024-09-20',
    segment: 'new',
    lifetimeValue: 450
  }
];

// Data Sources
export const mockDataSources: DataSource[] = [
  {
    id: 'source-1',
    name: 'Shopify Store',
    type: 'api',
    status: 'connected',
    lastSync: new Date(Date.now() - 15 * 60 * 1000).toISOString(),
    autoRefresh: true,
    refreshInterval: 15
  },
  {
    id: 'source-2',
    name: 'Google Analytics',
    type: 'api',
    status: 'connected',
    lastSync: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
    autoRefresh: true,
    refreshInterval: 60
  },
  {
    id: 'source-3',
    name: 'Facebook Ads',
    type: 'api',
    status: 'connected',
    lastSync: new Date(Date.now() - 120 * 60 * 1000).toISOString(),
    autoRefresh: true,
    refreshInterval: 120
  },
  {
    id: 'source-4',
    name: 'Sales Database',
    type: 'database',
    status: 'connected',
    lastSync: new Date(Date.now() - 5 * 60 * 1000).toISOString(),
    autoRefresh: true,
    refreshInterval: 5
  }
];






